import { zodResolver } from '@hookform/resolvers/zod'
import type { RefObject } from 'react'
import { type SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { TextArea } from '@/components/commons/textarea'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Stars01Icon, User01Icon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import {
	ExperienceMerchandisingInputSchema,
	type ExperienceShellMerchOutputSchema
} from '@/features/experience-vetting/form-schemas'
import { OpsVettingMerchFormHeader } from '@/features/ops-vetting/components/ops-vetting-merch-form-header'
import { useTranslation } from '@/translations/i18n'

interface ExpShellMerchInputFormProps {
	experienceId?: number
	isTraining?: boolean
	aiResponse: ExperienceShellMerchOutputSchema | undefined
	isReadOnly?: boolean
	defaultValues: Partial<ExperienceMerchandisingInputSchema>
	aiSubmissionRef: RefObject<HTMLButtonElement | null>
	handleSaveCb?: (data: ExperienceMerchandisingInputSchema) => void
	handleAiSubmissionCb: SubmitHandler<ExperienceMerchandisingInputSchema>
}

export const ExpShellMerchInputForm = ({
	isTraining = false,
	isReadOnly = false,
	aiResponse,
	defaultValues,
	aiSubmissionRef,
	handleSaveCb,
	handleAiSubmissionCb
}: ExpShellMerchInputFormProps) => {
	const { t } = useTranslation()
	const {
		register,
		control,
		formState: { errors },
		handleSubmit,
		getValues
	} = useForm({
		resolver: zodResolver(ExperienceMerchandisingInputSchema),
		defaultValues: defaultValues as ExperienceMerchandisingInputSchema
	})
	const { fields } = useFieldArray({ control, name: 'activities' })

	return (
		<form
			className='flex-1 p-6'
			onSubmit={handleSubmit(handleAiSubmissionCb)}
		>
			<OpsVettingMerchFormHeader
				description={t(
					'vetting:merch.experience.input_shell_form_description'
				)}
				endSlot={
					<div className='flex justify-end gap-2'>
						<ButtonLegacy
							onClick={() => aiSubmissionRef.current?.click()}
							type='button'
							variant='secondary'
						>
							<Stars01Icon />
							{aiResponse
								? t('vetting:merch.commons.regenerate')
								: t('vetting:merch.commons.improve_with_ai')}
						</ButtonLegacy>
						{!isTraining && handleSaveCb && (
							<ButtonLegacy
								onClick={() =>
									handleSaveCb(
										getValues() as ExperienceMerchandisingInputSchema
									)
								}
								type='button'
								variant='secondary'
							>
								{t('common:save')}
							</ButtonLegacy>
						)}
					</div>
				}
				icon={<User01Icon />}
				title={t('vetting:merch.experience.input_shell_form_title')}
			/>
			<div className='flex w-full flex-col gap-2'>
				<div className='flex w-full flex-col gap-5 py-6 text-neutral-900'>
					<h4 className='font-medium text-base'>
						{t('common:experience')}
					</h4>
					<InputText
						disabled={isReadOnly}
						label={t(
							'vetting:merch.experience.listing_title_label'
						)}
						type={'text'}
						{...register('listing_title')}
						errorMessage={errors?.listing_title?.message}
					/>
					<InputText
						disabled={isReadOnly}
						label={t('vetting:merch.experience.byline_label')}
						type={'text'}
						{...register('byline')}
						errorMessage={errors?.byline?.message}
					/>
					<TextArea
						disabled={isReadOnly}
						label={t(
							'vetting:merch.experience.location_description_label'
						)}
						rows={4}
						{...register('location_description')}
						errorMessage={errors?.location_description?.message}
					/>
					<TextArea
						disabled={isReadOnly}
						label={t(
							'vetting:merch.experience.what_guest_will_do_label'
						)}
						rows={13}
						{...register('guest_activities')}
						errorMessage={errors?.guest_activities?.message}
					/>

					{fields.map((activity, index) => (
						<div
							className='flex w-full flex-col gap-2'
							key={activity.id}
						>
							<div className='flex items-center justify-between gap-4'>
								<p className='font-medium text-sm'>
									{t(
										'vetting:merch.experience.activity_input_label',
										{
											index: index + 1
										}
									)}
								</p>
							</div>
							<InputText
								disabled={isReadOnly}
								label={t(
									'vetting:merch.experience.activity_title_label'
								)}
								type={'text'}
								{...register(`activities.${index}.title`)}
								errorMessage={
									errors?.activities?.[index]?.title?.message
								}
							/>
							<TextArea
								disabled={isReadOnly}
								label={t(
									'vetting:merch.experience.activity_description_label'
								)}
								rows={4}
								{...register(`activities.${index}.description`)}
								errorMessage={
									errors?.activities?.[index]?.description
										?.message
								}
							/>
						</div>
					))}
				</div>

				<Separator orientation='horizontal' />

				<div className='flex w-full flex-col gap-4 py-6 text-neutral-900'>
					<h4 className='font-medium text-base'>
						{t('common:host')}
					</h4>
					<InputText
						disabled={isReadOnly}
						label={t('vetting:merch.experience.first_name_label')}
						type={'text'}
						{...register('host_first_name')}
						errorMessage={errors?.host_first_name?.message}
					/>
					<TextArea
						disabled={isReadOnly}
						label={t('vetting:merch.experience.expertise_label')}
						rows={4}
						{...register('expertise_summary')}
						errorMessage={errors?.expertise_summary?.message}
					/>
					<TextArea
						disabled={isReadOnly}
						label={t(
							'vetting:merch.experience.recognition_general_label'
						)}
						rows={4}
						{...register('recognition_summary')}
						errorMessage={errors?.recognition_summary?.message}
					/>
				</div>
			</div>
			<button
				ref={aiSubmissionRef}
				style={{ display: 'none' }}
				type='submit'
			/>
		</form>
	)
}

ExpShellMerchInputForm.displayName = 'ExpShellMerchInputForm'
